import React, { useState, useRef, useEffect } from "react";
import "./clientEmailDisplay.css";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useMediaQuery } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_CLIENT_EMAILS } from "../../../../utils/GraphQL/queries";
import { CircularProgress } from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import EmailFlags from "../../adminComponents/EmailFlags/EmailFlags";
import EmailRow from "../EmailRow/EmailRow";
import EmailModal from "../EmailModal/EmailModal";
import SendEmailModal from "../SendEmail/SendEmailModal";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";

/*
 * Two hosts, one component.
 *
 * `inPanel` renders this straight into the client console's VS panel: no
 * BdrDisclosure wrapper (the panel header already names the section and only
 * one section is on screen at a time), open from the start, and none of the
 * document-scroll behaviour below — the console's page does not scroll, the
 * panel does, so `window.scrollTo` there would move the wrong thing or nothing.
 *
 * Without it, this is the old collapsible row on the Edit branch, unchanged.
 *
 * `onSummary` reports {count, unread} up. The list is fetched lazily, so the
 * Emails TILE cannot know its own count until this component has run — it is
 * the one tile that had no badge at all. Keep the parent's callback stable
 * (useCallback) or this loops.
 */
const ClientEmailDisplay = ({
  heightTrigger,
  clientEmails,
  inPanel,
  onSummary,
}) => {
  const emailsForQuery = Array.isArray(clientEmails)
    ? clientEmails.filter((e) => typeof e === "string" && e.trim().length > 0)
    : [];
  const { handleSnack } = useSnackbar();
  const [barCollapsed, setBarCollapsed] = useState(!inPanel);
  const [emailOpenId, setEmailOpenId] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [openSendReply, setOpenSendReply] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendReplyObj, setSendReplyObj] = useState(false);
  const [clientEmailArr, setClientEmailArr] = useState([]);
  // This list is fetched lazily on expand. Until the first fetch lands we do
  // not KNOW the count, and reporting 0 would render the section's empty state
  // — telling the user there are no emails when we simply have not looked.
  const [emailsFetched, setEmailsFetched] = useState(false);
  const [getClientEmailsGQL] = useLazyQuery(GET_CLIENT_EMAILS, {
    fetchPolicy: "network-only",
  });
  const smaller900 = useMediaQuery("(max-width:900px)");
  const clientEmailsDiv = useRef();

  // `clientEmailArr` carries year-divider pseudo-entries alongside the real
  // emails, so a raw length would over-count.
  const realEmails = clientEmailArr?.filter((item) => !item?.thisIsAYearParse);
  const emailCount = emailsFetched ? realEmails?.length : undefined;
  /*
   * INCOMING ONLY. "Unread" on a message the office SENT is meaningless - you
   * wrote it - and the list query does not even select `emailRead` on
   * `emailsSent`, so every sent message arrived `undefined` and counted as
   * unread. That is why this number ran far above anything a human would
   * count, and why it disagreed with the tile.
   *
   * Must stay identical to getClientEmailCounts on the server, which feeds
   * the same tile before this component has mounted.
   */
  const incoming = realEmails?.filter((email) => !email?.__isSent);
  const unreadEmails = emailsFetched
    ? incoming?.filter((email) => !email?.emailRead)?.length || 0
    : 0;
  /* Flag levels, matching the row classes: `!` is important, `!!` is urgent.
     The tile reports these rather than a total — a raw email count says nothing
     about whether the section needs you. */
  const flaggedEmails = emailsFetched
    ? incoming?.filter((email) => email?.flag === "!")?.length || 0
    : 0;
  const urgentEmails = emailsFetched
    ? incoming?.filter((email) => email?.flag === "!!")?.length || 0
    : 0;

  useEffect(() => {
    if (barCollapsed === true) {
      const id = setTimeout(() => {
        setLoading(false);
      }, 600);

      return () => clearTimeout(id);
    }


    if (barCollapsed === false) {
      if (emailsForQuery.length > 0) {
        setLoading(true);
        getEmails();
      } else {
        setLoading(false);
      }

      if (heightTrigger) {
        heightTrigger();
      }

      // The console's page is viewport-locked — the PANEL scrolls, not the
      // document — so none of the scroll-into-view work below applies there.
      if (inPanel) return undefined;

      const id = setTimeout(() => {
        if (smaller900 === true && barCollapsed === true) {
          const navHeight = document.querySelector(
            "#admin-nav-height-marker"
          )?.offsetHeight;
          const pagePos = clientEmailsDiv.current.offsetTop;

          const totalMobileScroll = pagePos - navHeight - 5;
          window.scrollTo(0, totalMobileScroll);
        }

        // if (smaller900 === false && barCollapsed === true) {
        //   clientEmailsDiv.current.scrollIntoView({ block: "start" });
        // }
      }, 500);

      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barCollapsed]);

  /* Hand the tile its count. `emailCount` is `undefined` until the first fetch
     lands, and that is not the same as 0 — a zero would render the empty
     treatment and claim the client has no emails when we simply have not
     looked yet. */
  useEffect(() => {
    // Only report a KNOWN count. The panel unmounts when you leave the tile, so
    // on every return `emailCount` is `undefined` again for the moment before
    // the refetch lands — reporting that would blank the tile's badge and then
    // bring it back, which is the flicker you see when clicking between tiles.
    if (!onSummary || !emailsFetched) return;
    onSummary({
      unread: unreadEmails,
      flagged: flaggedEmails,
      urgent: urgentEmails,
    });
  }, [emailsFetched, unreadEmails, flaggedEmails, urgentEmails, onSummary]);

  /* Lock + release in the cleanup, so the pair is balanced by construction.
     The old shape released on every render where the modal was shut —
     including before it had ever opened — which drives a shared count
     negative. See utils/bodyScrollLock.js. */
  useEffect(() => {
    if (!emailModalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [emailModalOpen]);

  const openBarCollapse = () => {
    setBarCollapsed((prev) => !prev);
  };

  const openEmail = (id) => {
    setEmailOpenId(id);
    setEmailModalOpen(true);
  };

  const closeEmail = (event) => {
    setEmailOpenId("");
    setEmailModalOpen(false);
    getEmails();
  };

  const getEmails = async () => {
    try {
      if (emailsForQuery.length === 0) {
        setClientEmailArr([]);
        setLoading(false);
        return;
      }

      const { data } = await getClientEmailsGQL({
        variables: {
          clientEmails: emailsForQuery,
        },
        fetchPolicy: "network-only",
      });

      const received = (data?.getClientEmails?.emails || []).map((e) => ({
        ...e,
        __isSent: false,
      }));
      const sent = (data?.getClientEmails?.emailsSent || []).map((e) => ({
        ...e,
        __isSent: true,
      }));
      const emailsArr = [...received, ...sent].sort(
        (a, b) => parseInt(b?.dateCreated) - parseInt(a?.dateCreated)
      );

      let newestYearFirst = 0;
      const parsedYears = [];

      for (let index = 0; index < emailsArr.length; index++) {
        const email = emailsArr[index];
        const date = new Date(parseInt(email?.dateCreated));

        if (index === 0) {
          newestYearFirst = date.getFullYear();
          parsedYears.push({ thisIsAYearParse: `${newestYearFirst}` });
          parsedYears.push(email);
          continue;
        }

        if (date.getFullYear() === newestYearFirst) {
          parsedYears.push(email);
          continue;
        }

        newestYearFirst = date.getFullYear();
        parsedYears.push({ thisIsAYearParse: `${newestYearFirst}` });
        parsedYears.push(email);
      }

      setClientEmailArr(parsedYears);
    } catch (err) {
      console.error("[ClientEmailDisplay] getEmails failed:", err?.message || err);
      setClientEmailArr([]);
      handleSnack?.("Failed to load client emails.", 4000);
    } finally {
      setLoading(false);
      // `finally` also runs for the early return above, so every path that
      // settles the list marks it known.
      setEmailsFetched(true);
    }
  };

  const prepareReplyEmail = (emailObj, emailObjIsSentObjBool) => {
    // if email obj is a sent obj, the reply to address needs to be the 'to' address of sent obj
    const sendTo = emailObjIsSentObjBool ? emailObj?.to : emailObj?.from;
    setSendReplyObj({
      to: sendTo,
      subject: `Re: ${emailObj?.emailSubject}`,
    });

    if (emailModalOpen) {
      closeEmail();
    }

    setOpenSendReply(true);
  };

  const changeParentForEmailModal = (bool) => {
    setOpenSendReply(bool);
  };

  const modals = (
    <>
      {emailModalOpen && (
        <EmailModal
          prepareReply={prepareReplyEmail}
          emailId={emailOpenId}
          closeEmail={closeEmail}
          changeEmail={openEmail}
        />
      )}
      {/* Send email modal for replies */}
      {openSendReply && (
        <SendEmailModal
          changeParent={changeParentForEmailModal}
          templateObj={sendReplyObj}
        />
      )}
    </>
  );

  const listBody = (
    <>
      {loading && (
        <div className="scc-emails-loading">
          <CircularProgress color="inherit" size={20} />
          <span>Loading emails…</span>
        </div>
        )}
        {!loading &&
          clientEmailArr?.length > 0 &&
          clientEmailArr?.map((email, index) => {
            // If 'email' is a date horizonal divider in the array
            if (email?.thisIsAYearParse) {
              return (
                <div key={`${email?.thisIsAYearParse}-${index}`} className="email-item-hor-year-divider">
                  {email?.thisIsAYearParse}
                </div>
              );
            }
            /* Date, sender parsing and the read/flag state classes live in
               `EmailRow` now. `client-read-email` is gone with them — it never
               had a rule of its own, it was a second name for `read-email`. */

            return (
              <EmailRow
                key={"emails-number:" + email?.id + index}
                email={email}
                onOpen={() => openEmail(email?.id)}
                actions={
                  /*
                    Both buttons stay VISIBLE here, outside `.email-row-quick`.
                    The Emails page hides its quick rail below 900px and leaves
                    an overflow menu behind; this panel is narrow by design and
                    has no overflow menu, so hiding Reply would strand it.
                  */
                  <>
                    <button
                      className="email-row-btn"
                      title="Reply"
                      onClick={(event) => {
                        event.stopPropagation();
                        prepareReplyEmail(email, email?.__isSent);
                      }}
                    >
                      <ReplyIcon sx={{ fontSize: "18px" }} />
                    </button>
                    <EmailFlags
                      emailRead={email?.emailRead}
                      getSelectedEmails={getEmails}
                      handleSnack={handleSnack}
                      emailId={email?.id}
                    />
                  </>
                }
              />
            );
          })}
      {!loading && clientEmailArr?.length === 0 && (
        <div className="scc-emails-empty">No emails to display</div>
      )}
    </>
  );

  if (inPanel) {
    return (
      <div className="scc-emails" ref={clientEmailsDiv}>
        {modals}
        {listBody}
      </div>
    );
  }

  return (
    <div
      id="client-pics-height-marker"
      className="client-pics-container"
      ref={clientEmailsDiv}
    >
      {modals}
      <BdrDisclosure
        open={!barCollapsed}
        onToggle={openBarCollapse}
        label="Emails"
        icon={<MailOutlineIcon />}
        count={emailCount}
        status={
          unreadEmails > 0
            ? { tone: "accent", text: `${unreadEmails} unread` }
            : undefined
        }
        emptyText="No emails yet"
      >
        {listBody}
      </BdrDisclosure>
    </div>
  );
};

export default ClientEmailDisplay;
